import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { Toggle } from '../ui/Toggle';
import { useTapOnce } from '../ui/useTapOnce';
import { sendOnce } from '../tickets/sendOnce';

/**
 * Whether the customer's copy of an invoice shows its hours (Inventory v2 ticket 13, the owner
 * 2026-10-09: "the charge, not the hours, unless a job is billed by the hour"). Only on an invoice
 * that bills time. On a draft the office switches it; issued, it says which the customer got. The
 * office's own page keeps every line either way. Nothing is decided here: the server folds the
 * customer's copy (`customerLines`).
 */
export function ShowHoursBar({
  invoice,
  lines,
  canSwitch,
}: {
  invoice: { id: string; status: string; showHours: boolean };
  lines: { timeEntryId: string | null }[];
  canSwitch: boolean;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  // One write per tap: a second flip before the first lands is dropped, not sent the other way.
  const lock = useTapOnce();
  const set = useMutation(
    trpc.invoice.setShowHours.mutationOptions({
      onSettled: () => {
        lock.release();
        void qc.invalidateQueries();
      },
    }),
  );
  if (!lines.some((l) => l.timeEntryId)) return null;
  return (
    <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-[var(--radius-md)] border border-border bg-surface-2 p-2 text-xs">
      {invoice.status === 'draft' && canSwitch ? (
        <Toggle
          label="Show hours to the customer"
          on={invoice.showHours}
          disabled={set.isPending}
          focusableWhenDisabled
          onChange={(on) =>
            sendOnce(lock, set.mutate, { invoiceId: invoice.id, showHours: on })
          }
        />
      ) : (
        <span className="font-semibold">
          {invoice.showHours
            ? 'Hours shown to the customer'
            : 'Hours hidden from the customer'}
        </span>
      )}
      <span className="text-muted-foreground">
        {invoice.showHours
          ? 'The customer sees each time entry with its hours and rate.'
          : 'The customer sees one Labor line with its amount. The hours and rates below are for the office.'}
      </span>
      {set.error ? (
        <span role="alert" className="basis-full text-[var(--sw-danger-text)]">
          {set.error.message}
        </span>
      ) : null}
    </div>
  );
}
